'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Checkbox } from '@common/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AGREEMENT_TYPE, AuthApi, AuthQueryOptions, TERM_CATEGORY } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ROUTES, SEARCH_PARAMS, STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useGlobalStore } from '@/stores/GlobalStore';
import { DataErrorFallback } from '../DataErrorFallback';
import { LoadingPageTemplate } from './LoadingPageTemplate';
export const PolicyFilterBottomDrawer = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DataErrorFallback />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ErrorBoundary fallback={t0}><Suspense fallback={<></>}><PolicyFilterBottomDrawerSuspense /></Suspense></ErrorBoundary>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PolicyFilterBottomDrawerSuspense = () => {
  const $ = _c(25);
  const [isPrivateChecked, setIsPrivateChecked] = useState(false);
  const [isSensitiveChecked, setIsSensitiveChecked] = useState(false);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get(SEARCH_PARAMS.POLICY_FILTER);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const policyFilter = t0;
  const setTermsDrawerType = useGlobalStore(_temp);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...AuthQueryOptions["get:/wello/v2/auth/member-wello/private_data_consent"](),
      select: _temp2
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const privateDataConsentQuery = useQuery(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ...AuthQueryOptions["get:/wello/v2/auth/member-term-agreement"]({
        query: {
          agreementTypeCd: AGREEMENT_TYPE.SENSITIVE_INFO_USE_POLICY_SELECT,
          termCategoryId: TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY
        }
      })
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: data_0
  } = useQuery(t2);
  const isPrivateDataConsentChecked = privateDataConsentQuery.data;
  const isSensitiveInfoUsePolicyChecked = data_0?.context?.agreeYN ?? false;
  const isPolicyFilterOpened = !isSensitiveInfoUsePolicyChecked && policyFilter === "true";
  let t3;
  if ($[4] !== queryClient) {
    t3 = {
      mutationFn: _temp3,
      onSuccess: () => {
        queryClient.invalidateQueries();
      }
    };
    $[4] = queryClient;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const privateDataConsentMutation = useMutation(t3);
  let t4;
  if ($[6] !== queryClient) {
    t4 = {
      mutationFn: _temp4,
      onSuccess: () => {
        queryClient.invalidateQueries();
      }
    };
    $[6] = queryClient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    mutate: sensitiveInfoUsePolicyMutation,
    isPending: isSensitiveInfoUsePolicyPending
  } = useMutation(t4);
  const {
    push
  } = useCustomRouter();
  let t5;
  if ($[8] !== isPrivateDataConsentChecked || $[9] !== isSensitiveChecked || $[10] !== privateDataConsentMutation || $[11] !== sensitiveInfoUsePolicyMutation) {
    t5 = () => {
      privateDataConsentMutation.mutate(true);
      sensitiveInfoUsePolicyMutation({
        isChecked: isPrivateDataConsentChecked ? true : isSensitiveChecked
      });
      const url = new URL(window.location.href);
      url.searchParams.delete(SEARCH_PARAMS.POLICY_FILTER);
      history.replaceState(null, "", url.search);
    };
    $[8] = isPrivateDataConsentChecked;
    $[9] = isSensitiveChecked;
    $[10] = privateDataConsentMutation;
    $[11] = sensitiveInfoUsePolicyMutation;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleSubmit = t5;
  const isLoading = privateDataConsentMutation.isPending || privateDataConsentQuery.isLoading || isSensitiveInfoUsePolicyPending;
  let t6;
  if ($[13] !== handleSubmit || $[14] !== isLoading || $[15] !== isPolicyFilterOpened || $[16] !== isPrivateChecked || $[17] !== isPrivateDataConsentChecked || $[18] !== isSensitiveChecked || $[19] !== isSensitiveInfoUsePolicyChecked || $[20] !== privateDataConsentMutation || $[21] !== privateDataConsentQuery.isFetched || $[22] !== push || $[23] !== setTermsDrawerType) {
    t6 = privateDataConsentQuery.isFetched ? <ResponsiveDialog opened={isPolicyFilterOpened || !(isPrivateDataConsentChecked ?? true)} drawerType="bottom"><div className="flex flex-col"><h2 className="mb-8 font-700 text-Gray-900 typo-body2">정책 추천을 위해 동의해주세요!</h2><p className="mb-16 text-Gray-700 typo-body4">동의하시면 맞춤정책을 추천 받을 수 있어요.</p>{isPrivateDataConsentChecked ? null : <p className="text-Gray-black typo-body4"><Checkbox checked={isPrivateDataConsentChecked || isPrivateChecked} onCheckedChange={() => {
            setIsPrivateChecked(!isPrivateChecked);
          }}><span className="font-500">(필수)</span> 개인정보 수집 및 이용 동의<button className="ml-4 font-600 underline" type="button" onClick={() => setTermsDrawerType(TERM_CATEGORY.FIT_PRIVACY_USE_POLICY_SELECT)}>보기</button></Checkbox></p>}<p className="mb-28 text-Gray-black typo-body4"><Checkbox checked={isSensitiveChecked || isSensitiveInfoUsePolicyChecked} onCheckedChange={() => {
            setIsSensitiveChecked(!isSensitiveChecked);
          }}><span className="font-500">(선택)</span> 민감정보 수집 및 이용 동의<button className="ml-4 font-600 underline" type="button" onClick={() => setTermsDrawerType(TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY)}>보기</button></Checkbox></p><Button disabled={!(isPrivateChecked || isPrivateDataConsentChecked)} loading={isLoading} onClick={handleSubmit}>동의하기</Button><Button loading={isLoading} theme="sub-text" onClick={async () => {
          if (isPrivateDataConsentChecked) {
            const url_0 = new URL(window.location.href);
            url_0.searchParams.delete(SEARCH_PARAMS.POLICY_FILTER);
            history.replaceState(null, "", url_0.search);
            return;
          }
          await privateDataConsentMutation.mutateAsync(false);
          const policyFilterLandingFrom = sessionStorage.getItem(STORAGE_KEY.POLICY_FILTER_LANDING_FROM);
          sessionStorage.removeItem(STORAGE_KEY.POLICY_FILTER_LANDING_FROM);
          push(policyFilterLandingFrom || ROUTES.HOME.pathname);
        }}>다음에 하기</Button></div></ResponsiveDialog> : <LoadingPageTemplate />;
    $[13] = handleSubmit;
    $[14] = isLoading;
    $[15] = isPolicyFilterOpened;
    $[16] = isPrivateChecked;
    $[17] = isPrivateDataConsentChecked;
    $[18] = isSensitiveChecked;
    $[19] = isSensitiveInfoUsePolicyChecked;
    $[20] = privateDataConsentMutation;
    $[21] = privateDataConsentQuery.isFetched;
    $[22] = push;
    $[23] = setTermsDrawerType;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
};
function _temp(state) {
  return state.setTermsDrawerType;
}
function _temp2(data) {
  return data.context?.privateDataConsentYN ?? false;
}
function _temp3(isAgree) {
  return AuthApi["put:/wello/v2/auth/member-wello/private_data_consent"]({
    body: {
      privateDataConsentYN: isAgree
    }
  });
}
function _temp4(t0) {
  const {
    isChecked
  } = t0;
  return AuthApi["post:/wello/v2/auth/member-term-agreement"]({
    body: {
      agreeYN: isChecked,
      agreementTypeCd: AGREEMENT_TYPE.SENSITIVE_INFO_USE_POLICY_SELECT,
      termCategoryId: TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY
    }
  });
}